import type { EntryValueViewT } from "@patient-watch/types";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { formatEntryValuesPageHeading } from "../formatEntryValuesPageHeading";
import type { EntryValuePageGroup } from "../entryValuesPageGroups";
import type { EntryCardEntryValuesVariant } from "./EntryCardEntryValues";
import { groupCheckboxRuns } from "../checkboxRuns";
import { foldMeasurementRuns, isNumberAnswer } from "../measurementRuns";
import type { ImageCountGroupT } from "../imageCountGroups";

export function questionGroupsWrapperClass(variant: EntryCardEntryValuesVariant): string {
    return cn("flex flex-col", variant === "plain" ? "gap-5" : "gap-4");
}

export function pageGroupClass(variant: EntryCardEntryValuesVariant, isFirst: boolean): string {
    if (variant === "plain") {
        return cn("space-y-2", !isFirst && "pt-4 border-slate-200 dark:border-slate-700/70 border-t");
    }
    return cn("space-y-2", !isFirst && "pt-4 border-slate-200 border-t");
}

export function pageHeadingClass(variant: EntryCardEntryValuesVariant): string {
    if (variant === "plain") {
        return "font-semibold text-teal-700 dark:text-teal-400 text-xs uppercase tracking-wider";
    }
    return cn("font-medium text-slate-900 dark:text-slate-300 text-lg");
}

export function questionsListClass(variant: EntryCardEntryValuesVariant, nestedUnderPageHeading: boolean): string {
    return cn("flex flex-col", variant === "plain" ? (nestedUnderPageHeading ? "gap-2" : "gap-1.5") : "gap-2");
}

export function questionRowClass(variant: EntryCardEntryValuesVariant): string {
    if (variant === "plain") {
        return cn(
            "flex flex-row gap-4 w-full font-normal text-slate-700",
            "bg-slate-50/90 dark:bg-slate-900/40 px-2.5 py-1.5 border border-slate-100 dark:border-slate-800/80 rounded-md text-sm"
        );
    }
    return cn(
        "flex flex-row gap-4 dark:bg-slate-800/50 p-2 border border-slate-200 rounded-md font-medium text-slate-700",
        "text-sm"
    );
}

interface EntryValuesQuestionGroupsProps {
    groupedByPage: EntryValuePageGroup[];
    showPageHeadings: boolean;
    variant: EntryCardEntryValuesVariant;
    renderRow: (entryValue: EntryValueViewT) => ReactNode;
    /** Renders a folded multi-select list. Falls back to one row per checkbox when omitted. */
    renderCheckboxGroup?: (entryValues: EntryValueViewT[]) => ReactNode;
    /** Renders joined number answers from the same page. Falls back to one row per number when omitted. */
    renderMeasurementGroup?: (entryValues: EntryValueViewT[]) => ReactNode;
    /** Image-and-count pages, drawn once per page in place of their loose answers. */
    imageCountGroups?: ImageCountGroupT[];
    renderImageCountGroup?: (group: ImageCountGroupT) => ReactNode;
    /** Flat list when page headings are hidden. */
    flatValues?: EntryValueViewT[];
    /** Action for a whole page of answers (for example Edit), shown beside the page's heading or answers. */
    renderPageAction?: (group: EntryValuePageGroup) => ReactNode;
}

export function EntryValuesQuestionGroups({
    groupedByPage,
    showPageHeadings,
    variant,
    renderRow,
    renderCheckboxGroup,
    renderMeasurementGroup,
    imageCountGroups = [],
    renderImageCountGroup,
    flatValues = [],
    renderPageAction,
}: EntryValuesQuestionGroupsProps) {
    /** Image groups whose answers are all in this list. Their answers are drawn by the group, not as rows. */
    const imageGroupsIn = (values: EntryValueViewT[]) =>
        renderImageCountGroup
            ? imageCountGroups.filter((group) =>
                  group.rows.some((row) => values.some((ev) => ev.entry_value_id === row.entryValue.entry_value_id))
              )
            : [];

    const renderRows = (values: EntryValueViewT[]) => {
        const imageGroups = imageGroupsIn(values);
        const groupedIds = new Set(
            imageGroups.flatMap((group) => group.rows.map((row) => row.entryValue.entry_value_id))
        );
        const restRows = values.filter((ev) => !groupedIds.has(ev.entry_value_id));

        return [
            ...imageGroups.map((group) => <li key={group.key}>{renderImageCountGroup!(group)}</li>),
            ...foldMeasurementRuns(groupCheckboxRuns(restRows)).map((row) =>
                row.kind === "checkboxes" && renderCheckboxGroup ? (
                    <li key={row.entryValues[0]?.entry_value_id}>{renderCheckboxGroup(row.entryValues)}</li>
                ) : row.kind === "checkboxes" ? (
                    row.entryValues.map((ev) => <li key={ev.entry_value_id}>{renderRow(ev)}</li>)
                ) : row.kind === "measurements" && renderMeasurementGroup ? (
                    <li key={row.entryValues[0]?.entry_value_id}>{renderMeasurementGroup(row.entryValues)}</li>
                ) : row.kind === "measurements" ? (
                    row.entryValues.map((ev) => <li key={ev.entry_value_id}>{renderRow(ev)}</li>)
                ) : (
                    <li key={row.entryValue.entry_value_id}>{renderRow(row.entryValue)}</li>
                )
            ),
        ];
    };

    if (showPageHeadings && groupedByPage.length > 0) {
        return (
            <div className={questionGroupsWrapperClass(variant)}>
                {groupedByPage.map((group, index) => {
                    const headingUsedAsRowLabel =
                        group.values.length > 0 &&
                        group.values.every(isNumberAnswer) &&
                        imageGroupsIn(group.values).length === 0;
                    const hasHeading = group.pageIndex != null && !headingUsedAsRowLabel;
                    const pageAction = renderPageAction?.(group);
                    const list = (
                        <ul
                            className={cn(
                                questionsListClass(variant, hasHeading),
                                pageAction && !hasHeading && "flex-1"
                            )}
                        >
                            {renderRows(group.values)}
                        </ul>
                    );
                    return (
                        <div key={group.pageIndex ?? "ungrouped"} className={pageGroupClass(variant, index === 0)}>
                            {hasHeading ? (
                                <div className="flex justify-between items-center gap-2">
                                    <h4 className={pageHeadingClass(variant)}>
                                        {formatEntryValuesPageHeading(group.pageLabel, group.pageIndex!)}
                                    </h4>
                                    {pageAction}
                                </div>
                            ) : null}
                            {pageAction && !hasHeading ? (
                                <div className="flex items-start gap-2">
                                    {list}
                                    {pageAction}
                                </div>
                            ) : (
                                list
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    const flatAction = groupedByPage.length === 1 ? renderPageAction?.(groupedByPage[0]!) : null;
    const flatList = (
        <ul className={cn(questionsListClass(variant, false), flatAction && "flex-1")}>{renderRows(flatValues)}</ul>
    );
    return flatAction ? (
        <div className="flex items-start gap-2">
            {flatList}
            {flatAction}
        </div>
    ) : (
        flatList
    );
}
